import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ASSETS, CONTACT } from "../lib/brand";

const STEPS = [
  {
    n: "01",
    title: "Fique de olho",
    desc: "Eu aviso no Instagram sempre que abro uma live shop — data, hora e o que vou apresentar.",
  },
  {
    n: "02",
    title: "Entre ao vivo",
    desc: "No horário combinado, entre no meu Instagram. A conversa acontece na hora, com peças, história e curadoria em tempo real.",
  },
  {
    n: "03",
    title: "Interaja pelo chat",
    desc: "Comente, pergunte, escolha. Eu atendo cada pessoa pelo chat e conduzo a experiência de forma pessoal — não é vitrine, é encontro.",
  },
];

export default function LiveShop() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <section
      id="live-shop"
      ref={ref}
      data-testid="live-shop-section"
      className="relative bg-cream-100 py-24 sm:py-40 border-t border-ink-800/15 overflow-hidden"
    >
      {/* Background italic word */}
      <div
        aria-hidden
        className="absolute -bottom-10 -left-6 pointer-events-none select-none opacity-[0.06]"
      >
        <span
          className="font-serif italic leading-none text-brick-500"
          style={{ fontSize: "clamp(10rem, 26vw, 28rem)" }}
        >
          live
        </span>
      </div>

      <div className="relative mx-auto max-w-[1600px] px-6 sm:px-10">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 lg:gap-16 items-end mb-16 sm:mb-20">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/60 mb-8">
              <span className="w-8 h-px bg-brick-500" />
              <span>N.º 005 — Live Shop</span>
              <span className="inline-flex items-center gap-2 text-brick-500">
                <span className="w-1.5 h-1.5 bg-brick-500 rounded-full animate-pulse" />
                on air
              </span>
            </div>

            <div className="overflow-hidden">
              <motion.h2
                initial={{ y: "110%" }}
                animate={inView ? { y: 0 } : {}}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif text-ink-800 leading-[0.88] tracking-tight"
                style={{ fontSize: "clamp(3rem, 8vw, 8.5rem)" }}
              >
                Ao vivo.
              </motion.h2>
            </div>
            <div className="overflow-hidden mt-1">
              <motion.h2
                initial={{ y: "110%" }}
                animate={inView ? { y: 0 } : {}}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
                className="font-serif italic font-medium text-brick-500 leading-[0.88] tracking-tight"
                style={{ fontSize: "clamp(3rem, 8vw, 8.5rem)" }}
              >
                Direto na tela.
              </motion.h2>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:col-start-9">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.9, delay: 0.55 }}
              className="font-sans text-base sm:text-lg leading-relaxed text-ink-800/70"
            >
              A <span className="italic-serif text-brick-500">Live Shop</span> é
              o meu formato autoral: uma experiência ao vivo pelo Instagram,
              onde a curadoria acontece diante da câmera — com conversa, história
              e escolha em tempo real.
            </motion.p>
          </div>
        </div>

        {/* Split: steps + video card */}
        <div className="grid grid-cols-12 gap-8 lg:gap-16 items-start">
          {/* Steps */}
          <ol
            className="col-span-12 lg:col-span-6 divide-y divide-ink-800/15 border-t border-b border-ink-800/15"
            data-testid="live-shop-steps"
          >
            {STEPS.map((s, i) => (
              <motion.li
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  duration: 0.7,
                  delay: 0.7 + i * 0.12,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="grid grid-cols-12 gap-6 py-8 sm:py-10 items-start group"
              >
                <span
                  className="col-span-2 sm:col-span-1 font-serif text-brick-500 leading-none"
                  style={{ fontSize: "clamp(2rem, 3.2vw, 3rem)" }}
                >
                  {s.n}
                </span>
                <div className="col-span-10 sm:col-span-11">
                  <h3
                    className="font-serif text-ink-800 leading-none"
                    style={{ fontSize: "clamp(1.75rem, 2.6vw, 2.5rem)" }}
                  >
                    {s.title}
                  </h3>
                  <p className="mt-3 font-sans text-base leading-relaxed text-ink-800/70 max-w-lg">
                    {s.desc}
                  </p>
                </div>
              </motion.li>
            ))}
          </ol>

          {/* Video card */}
          <div className="col-span-12 lg:col-span-6 lg:pl-8">
            <motion.a
              href={CONTACT.liveShop}
              target="_blank"
              rel="noreferrer"
              aria-label="Assistir como funciona a Live Shop"
              data-testid="live-shop-cta"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              className="group relative block bg-brick-500 text-cream border border-brick-500 overflow-hidden"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-brick-600">
                <img
                  src={ASSETS.rack}
                  alt="Prévia — Live Shop"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105 opacity-95"
                />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(138,30,26,0.15) 30%, rgba(65,12,9,0.75) 100%)",
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
                    <motion.span
                      aria-hidden
                      className="absolute inset-0 rounded-full border border-cream"
                      animate={{ scale: [1, 1.35, 1], opacity: [0.65, 0, 0.65] }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                    />
                    <motion.span
                      aria-hidden
                      className="absolute inset-0 rounded-full border border-cream/60"
                      animate={{ scale: [1, 1.6, 1], opacity: [0.4, 0, 0.4] }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", delay: 0.6 }}
                    />
                    <span className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-cream text-brick-500 flex items-center justify-center group-hover:bg-ink-800 group-hover:text-cream transition-colors duration-500">
                      <svg
                        viewBox="0 0 24 24"
                        width="26"
                        height="26"
                        fill="currentColor"
                        aria-hidden="true"
                        className="ml-1"
                      >
                        <path d="M8 5.14v13.72a1 1 0 001.55.83l10.03-6.86a1 1 0 000-1.66L9.55 4.31A1 1 0 008 5.14z" />
                      </svg>
                    </span>
                  </div>
                </div>
                <div className="absolute top-3 left-3 right-3 flex justify-between font-mono text-[9px] uppercase tracking-[0.3em] text-cream">
                  <span className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-cream rounded-full animate-pulse" />
                    Live · em tempo real
                  </span>
                  <span>HL / Live Shop</span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-4 px-5 sm:px-6 py-5 border-t border-cream/15">
                <div className="flex flex-col">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/80">
                    Stories · destacados
                  </span>
                  <span
                    className="mt-1 font-serif italic leading-none text-cream"
                    style={{ fontSize: "clamp(1.35rem, 1.7vw, 1.75rem)" }}
                  >
                    Ver como funciona
                  </span>
                </div>
                <span className="font-serif italic text-3xl text-cream group-hover:translate-x-1 transition-transform duration-500">
                  →
                </span>
              </div>
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
}
